import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole = "admin" | "user";

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [tier, setTier] = useState<"free" | "pro">("free");

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setRoles([]);
      setTier("free");
      return;
    }
    (async () => {
      const [{ data: r }, { data: pro }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        supabase.rpc("is_pro_user", { _uid: user.id }),
      ]);
      setRoles(((r ?? []) as { role: AppRole }[]).map((x) => x.role));
      setTier(pro ? "pro" : "free");
    })();
  }, [user]);

  return {
    session,
    user,
    loading,
    roles,
    tier,
    isAdmin: roles.includes("admin"),
    isPro: tier === "pro",
    signOut: () => supabase.auth.signOut(),
  };
}
